/**
 * BuyElectro compare.
 *
 * Up to four products, saved in this browser's localStorage the same way as the wishlist
 * (assets/wishlist.js): product handles only, in the order they were added. /pages/compare lays
 * them out side by side. Each product is rendered by Shopify through the Section Rendering API
 * (/products/<handle>?sections=compare-column, sections/compare-column.liquid), so price, stock
 * and specs are always current; this script lines their spec sheets up into one table.
 *
 * Hooks
 *   [data-compare-toggle][data-product-handle]   compare buttons (product cards, product page, quick view)
 *   [data-compare-remove][data-product-handle]   remove buttons on the compare page
 *   [data-compare-count]                          header / bottom-nav badges
 *   [data-compare-page]                           the page (snippets/compare-page.liquid)
 *
 * API: window.BECompare = { max, items, has, add, remove, toggle, clear }
 */
(function () {
	'use strict';

	if (window.BECompare) return;

	var STORAGE_KEY = 'buyelectro:compare';
	var MAX = 4;
	var COLUMN_SECTION = 'compare-column';
	var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
	var compareUrl = shopRoot + 'pages/compare';

	var memoryCopy = null; // used when localStorage is unavailable (private mode, blocked storage)

	/* ───────────────────────── storage ───────────────────────── */

	function isItem(item) {
		return item && typeof item.handle === 'string' && item.handle.length > 0;
	}

	function read() {
		try {
			var raw = window.localStorage.getItem(STORAGE_KEY);
			var list = raw ? JSON.parse(raw) : [];
			return Array.isArray(list) ? list.filter(isItem).slice(0, MAX) : [];
		} catch (error) {
			return (memoryCopy || []).slice();
		}
	}

	function write(list) {
		memoryCopy = list.slice();
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
		} catch (error) {
			/* storage blocked: the in-memory copy keeps this page working */
		}
		changed();
	}

	function has(handle) {
		return read().some(function (item) {
			return item.handle === handle;
		});
	}

	/** @returns 'added' | 'exists' | 'full' */
	function add(handle, index) {
		if (!handle) return 'exists';
		var list = read();
		if (has(handle)) return 'exists';
		if (list.length >= MAX) return 'full';
		var entry = { handle: handle, addedAt: Date.now() };
		if (typeof index === 'number' && index >= 0 && index <= list.length) {
			list.splice(index, 0, entry);
		} else {
			list.push(entry); // columns run left to right in the order products were added
		}
		write(list);
		return 'added';
	}

	/** @returns the index it had, or -1 */
	function remove(handle) {
		var list = read();
		var index = -1;
		list.forEach(function (item, i) {
			if (item.handle === handle) index = i;
		});
		if (index === -1) return -1;
		list.splice(index, 1);
		write(list);
		return index;
	}

	function toggle(handle) {
		if (has(handle)) {
			remove(handle);
			return false;
		}
		return add(handle) === 'added';
	}

	function clear() {
		var previous = read();
		if (previous.length) write([]);
		return previous;
	}

	/* ───────────────────────── buttons + badges ───────────────────────── */

	function paint(element, on) {
		element.setAttribute('aria-pressed', on ? 'true' : 'false');
		element.classList.toggle('is-active', on);

		var label = element.querySelector('[data-compare-label]');
		if (label) {
			label.textContent = on
				? element.getAttribute('data-label-remove') || 'Remove from compare'
				: element.getAttribute('data-label-add') || 'Compare';
		}
		if (element.hasAttribute('title')) {
			element.setAttribute('title', on ? 'Remove from compare' : 'Compare');
		}
	}

	function sync() {
		var saved = {};
		var list = read();
		list.forEach(function (item) {
			saved[item.handle] = true;
		});

		document.querySelectorAll('[data-compare-toggle]').forEach(function (element) {
			paint(element, !!saved[element.getAttribute('data-product-handle')]);
		});
		document.querySelectorAll('[data-compare-count]').forEach(function (badge) {
			badge.textContent = list.length;
		});
	}

	function changed() {
		sync();
		renderPage();
		document.dispatchEvent(new CustomEvent('compare:change', { detail: { items: read() } }));
	}

	function toast(message, options) {
		if (window.BuyElectro && typeof window.BuyElectro.toast === 'function') {
			window.BuyElectro.toast(message, options);
		}
	}

	function pageRoot() {
		return document.querySelector('[data-compare-page]');
	}

	function addWithFeedback(handle) {
		var result = add(handle);
		if (result === 'full') {
			toast('You can compare up to ' + MAX + ' products. Remove one to add another.', {
				duration: 6000,
				action: pageRoot() ? null : { label: 'Compare', href: compareUrl },
			});
		} else if (result === 'added') {
			var count = read().length;
			toast(
				'Added to compare (' + count + ' of ' + MAX + ')',
				pageRoot() ? {} : { action: { label: count > 1 ? 'Compare now' : 'View', href: compareUrl } }
			);
		}
	}

	function removeWithUndo(handle) {
		var index = remove(handle);
		if (index === -1) return;
		toast('Removed from compare', {
			duration: 6000, // long enough to actually reach the Undo button
			action: {
				label: 'Undo',
				onClick: function () {
					add(handle, index);
				},
			},
		});
	}

	/* ───────────────────────── compare page ───────────────────────── */

	// handle -> { state: 'loading' | 'ready' | 'missing' | 'error', summary, facts, specs }
	var columns = {};

	function columnUrl(handle) {
		return shopRoot + 'products/' + encodeURIComponent(handle) + '?sections=' + COLUMN_SECTION;
	}

	function load(handle) {
		columns[handle] = { state: 'loading' };
		// No `Accept: application/json` header: on a product URL Shopify answers that with the
		// product's JSON and ignores ?sections= entirely.
		fetch(columnUrl(handle))
			.then(function (response) {
				if (response.status === 404) return null;
				if (!response.ok) throw new Error('HTTP ' + response.status);
				return response.json().then(function (data) {
					return (data && data[COLUMN_SECTION]) || null;
				});
			})
			.then(function (html) {
				columns[handle] = parseColumn(html);
			})
			.catch(function () {
				// Never drop a saved product because of a network hiccup — offer a retry.
				columns[handle] = { state: 'error' };
			})
			.then(function () {
				if (has(handle)) renderPage();
			});
	}

	function text(node) {
		return (node ? node.textContent : '').replace(/\s+/g, ' ').trim();
	}

	function parseColumn(html) {
		var column = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('[data-compare-column]') : null;
		if (!column) return { state: 'missing' };

		var facts = Array.prototype.map.call(column.querySelectorAll('[data-compare-fact]'), function (fact) {
			var value = fact.querySelector('[data-compare-value]');
			return { group: 'Overview', label: fact.getAttribute('data-label'), node: value, text: text(value) };
		});

		// The spec sheet (snippets/extra-specs.liquid): <div class="spec-section"><h4>Display</h4>
		// <ul><li><strong>Screen Size:</strong> 6.78 Inches</li>…
		var specs = [];
		column.querySelectorAll('[data-compare-specs] .spec-section').forEach(function (section) {
			var group = text(section.querySelector('h4')) || 'Specifications';
			section.querySelectorAll('li').forEach(function (item) {
				var strong = item.querySelector('strong');
				var label = text(strong).replace(/:\s*$/, '');
				if (!label) return;
				var value = text(item).slice(text(strong).length).trim();
				specs.push({ group: group, label: label, text: value || '—' });
			});
		});

		return {
			state: 'ready',
			summary: column.querySelector('[data-compare-summary]'),
			facts: facts,
			specs: specs,
		};
	}

	function el(tag, className, content) {
		var node = document.createElement(tag);
		if (className) node.className = className;
		if (content != null) node.textContent = content;
		return node;
	}

	function placeholderCell(handle, state) {
		var box = el('div', 'be-compare-product be-compare-product--' + state);
		if (state === 'loading') {
			box.setAttribute('aria-busy', 'true');
			box.appendChild(el('span', 'be-compare-skeleton be-compare-skeleton--media'));
			box.appendChild(el('span', 'be-compare-skeleton'));
			box.appendChild(el('span', 'be-compare-skeleton be-compare-skeleton--short'));
			return box;
		}
		box.appendChild(el('p', 'be-compare-product__message', state === 'missing' ? 'This product is no longer available.' : 'We could not load this product.'));
		var button = el('button', 'be-compare-btn be-compare-btn--ghost', state === 'missing' ? 'Remove' : 'Try again');
		button.type = 'button';
		button.setAttribute(state === 'missing' ? 'data-compare-remove' : 'data-compare-retry', '');
		button.setAttribute('data-product-handle', handle);
		box.appendChild(button);
		return box;
	}

	function normalise(value) {
		return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
	}

	function buildTable(root, list) {
		var table = root.querySelector('[data-compare-table]');
		var diffOnly = root.querySelector('[data-compare-diff]');
		table.textContent = '';
		table.style.setProperty('--be-compare-cols', list.length);

		// Header: one column per product
		var head = el('thead');
		var headRow = el('tr');
		var corner = el('th', 'be-compare__corner');
		corner.scope = 'col';
		corner.appendChild(el('span', 'be-compare__sr', 'Product'));
		headRow.appendChild(corner);

		list.forEach(function (item) {
			var column = columns[item.handle];
			var cell = el('th', 'be-compare__product');
			cell.scope = 'col';
			if (column && column.state === 'ready' && column.summary) {
				cell.appendChild(document.importNode(column.summary, true));
			} else {
				cell.appendChild(placeholderCell(item.handle, column ? column.state : 'loading'));
			}
			headRow.appendChild(cell);
		});
		head.appendChild(headRow);
		table.appendChild(head);

		// Rows: Overview first, then the spec groups in the order the products list them
		var groups = [];
		var groupIndex = {};
		function rowFor(groupName, label) {
			var group = groupIndex[groupName];
			if (!group) {
				group = groupIndex[groupName] = { name: groupName, rows: [], rowIndex: {} };
				groups.push(group);
			}
			var row = group.rowIndex[label];
			if (!row) {
				row = group.rowIndex[label] = { label: label, cells: {} };
				group.rows.push(row);
			}
			return row;
		}

		var ready = list.filter(function (item) {
			return columns[item.handle] && columns[item.handle].state === 'ready';
		});

		ready.forEach(function (item) {
			var column = columns[item.handle];
			column.facts.forEach(function (fact) {
				rowFor(fact.group, fact.label).cells[item.handle] = fact;
			});
			column.specs.forEach(function (spec) {
				var row = rowFor(spec.group, spec.label);
				if (!row.cells[item.handle]) row.cells[item.handle] = spec; // first mention wins
			});
		});

		groups.forEach(function (group) {
			var body = el('tbody');
			var groupRow = el('tr', 'be-compare__group');
			var groupCell = el('th');
			groupCell.scope = 'rowgroup';
			groupCell.colSpan = list.length + 1;
			groupCell.appendChild(el('span', 'be-compare__group-name', group.name));
			groupRow.appendChild(groupCell);
			body.appendChild(groupRow);

			var differences = 0;
			group.rows.forEach(function (row) {
				var tr = el('tr');
				var th = el('th', 'be-compare__label', row.label);
				th.scope = 'row';
				tr.appendChild(th);

				var values = [];
				list.forEach(function (item) {
					var td = el('td');
					var cell = row.cells[item.handle];
					if (cell && cell.node) {
						td.appendChild(document.importNode(cell.node, true));
					} else if (cell) {
						td.textContent = cell.text;
					} else if (columns[item.handle] && columns[item.handle].state === 'ready') {
						td.textContent = '—';
						td.className = 'be-compare__none';
					}
					if (columns[item.handle] && columns[item.handle].state === 'ready') {
						values.push(cell ? normalise(cell.text) : '');
					}
					tr.appendChild(td);
				});

				var same = values.length > 1 && values.every(function (value) {
					return value !== '' && value === values[0];
				});
				tr.setAttribute('data-same', same ? 'true' : 'false');
				if (!same) differences += 1;
				body.appendChild(tr);
			});

			body.setAttribute('data-same', differences === 0 ? 'true' : 'false');
			table.appendChild(body);
		});

		table.classList.toggle('is-differences-only', !!(diffOnly && diffOnly.checked && ready.length > 1));

		// The summaries in the table are copies of the loaded columns, so each copy needs its own
		// buy box started (snippets/inline-buy-box.liquid, assets/product-buy-box.js).
		startBuyBoxes(table);
	}

	function startBuyBoxes(scope) {
		if (!window.BEProductBuyBox || !scope) return;
		Array.prototype.forEach.call(scope.querySelectorAll('[data-product-root]'), function (root) {
			root.removeAttribute('data-buy-box-ready');
			window.BEProductBuyBox.init(root);
		});
	}

	function renderPage() {
		var root = pageRoot();
		if (!root) return;

		var list = read();
		var count = root.querySelector('[data-compare-page-count]');
		var empty = root.querySelector('[data-compare-empty]');
		var scroller = root.querySelector('[data-compare-scroller]');
		var tools = root.querySelector('[data-compare-tools]');
		var hint = root.querySelector('[data-compare-hint]');

		if (count) {
			count.textContent = list.length ? list.length + ' of ' + MAX + ' products' : '';
			count.hidden = list.length === 0;
		}
		if (empty) empty.hidden = list.length > 0;
		if (tools) tools.hidden = list.length === 0;
		if (hint) hint.hidden = list.length !== 1;
		if (scroller) scroller.hidden = list.length === 0;
		root.setAttribute('data-ready', '');

		// A removed product's column stays in memory, so Undo puts it straight back.
		list.forEach(function (item) {
			if (!columns[item.handle]) load(item.handle);
		});

		if (list.length) buildTable(root, list);
	}

	/* ───────────────────────── events ───────────────────────── */

	document.addEventListener('click', function (event) {
		var target = event.target;
		if (!(target instanceof Element)) return;

		var button = target.closest('[data-compare-toggle]');
		if (button) {
			event.preventDefault();
			var handle = button.getAttribute('data-product-handle');
			if (!handle) return;
			if (has(handle)) removeWithUndo(handle);
			else addWithFeedback(handle);
			return;
		}

		var removeButton = target.closest('[data-compare-remove]');
		if (removeButton) {
			event.preventDefault();
			removeWithUndo(removeButton.getAttribute('data-product-handle'));
			return;
		}

		var retry = target.closest('[data-compare-retry]');
		if (retry) {
			event.preventDefault();
			load(retry.getAttribute('data-product-handle'));
			renderPage();
			return;
		}

		if (target.closest('[data-compare-clear]')) {
			event.preventDefault();
			var previous = clear();
			if (!previous.length) return;
			toast('Compare list cleared', {
				duration: 6000,
				action: {
					label: 'Undo',
					onClick: function () {
						write(previous);
					},
				},
			});
		}
	});

	document.addEventListener('change', function (event) {
		var target = event.target;
		if (target instanceof Element && target.matches('[data-compare-diff]')) renderPage();
	});

	// Compare buttons on product cards are <span role="button">.
	document.addEventListener('keydown', function (event) {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		var target = event.target;
		if (!(target instanceof Element) || !target.matches('[data-compare-toggle][role="button"]')) return;
		event.preventDefault();
		target.click();
	});

	// Another tab changed the list.
	window.addEventListener('storage', function (event) {
		if (event.key === STORAGE_KEY) {
			sync();
			renderPage();
		}
	});

	// Buttons that arrive later (quick view, re-rendered sections) get painted too.
	var scheduled = false;
	function scheduleSync() {
		if (scheduled) return;
		scheduled = true;
		window.requestAnimationFrame(function () {
			scheduled = false;
			sync();
		});
	}

	function watch() {
		if (!window.MutationObserver) return;
		new MutationObserver(function (mutations) {
			for (var i = 0; i < mutations.length; i += 1) {
				var nodes = mutations[i].addedNodes;
				for (var j = 0; j < nodes.length; j += 1) {
					var node = nodes[j];
					if (
						node.nodeType === 1 &&
						(node.matches('[data-compare-toggle], [data-compare-count]') ||
							node.querySelector('[data-compare-toggle], [data-compare-count]'))
					) {
						scheduleSync();
						return;
					}
				}
			}
		}).observe(document.body, { childList: true, subtree: true });
	}

	function init() {
		sync();
		renderPage();
		watch();
	}

	window.BECompare = {
		max: MAX,
		items: read,
		has: has,
		add: add,
		remove: remove,
		toggle: toggle,
		clear: clear,
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
